/**
 * Profile Data Panel
 *
 * Right-side workspace panel (Kampagnen-Builder v2 layout): the user fills in
 * personal and company data (Schritt 1) in a form, saves it, and the chat bot
 * is informed so it can verify the data and suggest next steps.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { getUserInfo, changeUserInfo } from '../../../API/user';
import { getOwnCompanyData, changeOwnCompanyData } from '../../../API/company';
import useAIChatStore from '../../../stores/ai-chat-store';
import useCountries from '../../../hooks/use-countries';

/**
 * Land als ISO-Code speichern (wie die Firmenseite im Account). Früher war das hier ein
 * Freitextfeld — Einträge wie „Deutschland" oder „Deuschland" erkannte die Firmenseite nicht.
 */
const LEGACY_COUNTRY_CODES: Record<string, string> = {
  DEUTSCHLAND: 'DE',
  DEUSCHLAND: 'DE',
  GERMANY: 'DE',
  ÖSTERREICH: 'AT',
  OESTERREICH: 'AT',
  AUSTRIA: 'AT',
  SCHWEIZ: 'CH',
  SWITZERLAND: 'CH',
};
const normalizeCountry = (value: string): string => {
  const v = (value || '').trim();
  if (!v) return 'DE';
  return LEGACY_COUNTRY_CODES[v.toUpperCase()] || v;
};

interface ProfileDataPanelProps {
  /** 'panel' = eigenständiges rechtes Panel; 'workspace' = eingebettet im Arbeitsbereich (ohne eigene Breite/Header) */
  variant?: 'panel' | 'workspace';
  onClose: () => void;
  /** Called after a successful save with a short German/English summary of the saved fields. */
  onSaved: (summary: string) => void;
  /** Called when the save left no missing fields — the layout advances to the next step. */
  onComplete?: () => void;
}

interface ProfileFormState {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
}

const EMPTY_FORM: ProfileFormState = {
  firstName: '',
  lastName: '',
  email: '',
  companyName: '',
  street: '',
  postalCode: '',
  city: '',
  country: '',
};

const FIELD_LABELS: Record<keyof ProfileFormState, { de: string; en: string }> = {
  firstName: { de: 'Vorname', en: 'First name' },
  lastName: { de: 'Nachname', en: 'Last name' },
  email: { de: 'E-Mail-Adresse', en: 'Email address' },
  companyName: { de: 'Unternehmen', en: 'Company' },
  street: { de: 'Straße und Hausnummer', en: 'Street and number' },
  postalCode: { de: 'PLZ', en: 'Postal code' },
  city: { de: 'Stadt', en: 'City' },
  country: { de: 'Land', en: 'Country' },
};

const ProfileDataPanel: React.FC<ProfileDataPanelProps> = ({ variant = 'panel', onClose, onSaved, onComplete }) => {
  const { classes, cx } = useStyles();
  const { i18n } = useTranslation();
  const isDe = i18n.language === 'de';

  // Sprachumschalter: 'de', sobald die aktive Sprache mit 'de' beginnt (z.B. 'de-DE'), sonst 'en'.
  const uiLanguage = (i18n.language || '').toLowerCase().startsWith('de') ? 'de' : 'en';

  const handleUiLanguageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value === 'de' ? 'de' : 'en';
    if (next === uiLanguage) return;
    // Sofort lokal umschalten — der Wizard wechselt live die Sprache.
    void i18n.changeLanguage(next);
    // Danach still ins Profil persistieren (gleiche API wie die Kontoverwaltung).
    try {
      await changeUserInfo({ language: next });
    } catch {
      // Fehler bewusst ignorieren — die Sprache ist lokal trotzdem umgestellt.
    }
  };

  const dataVersion = useAIChatStore((state) => state.dataVersion);
  const [form, setForm] = useState<ProfileFormState>(EMPTY_FORM);
  const [initial, setInitial] = useState<ProfileFormState>(EMPTY_FORM);
  const [canEditCompany, setCanEditCompany] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [user, companyRes] = await Promise.all([getUserInfo(), getOwnCompanyData()]);
        if (cancelled) return;
        const company = companyRes?.data;
        // Firmendaten dürfen nur Admins/Owner ändern (gleiche Regel wie PATCH /tenants/current)
        const membership = (user?.tenants || []).find(
          (m: { tenantId?: string }) => m.tenantId === company?._id
        );
        const role = membership?.role || 'member';
        setCanEditCompany(role === 'admin' || role === 'owner');
        const loaded: ProfileFormState = {
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
          companyName: company?.name || '',
          street: company?.address?.addressLine1 || '',
          postalCode: company?.address?.postalCode || '',
          city: company?.address?.city || '',
          country: normalizeCountry(company?.address?.country || ''),
        };
        setForm(loaded);
        setInitial(loaded);
      } catch {
        if (!cancelled) setError(isDe ? 'Daten konnten nicht geladen werden.' : 'Could not load data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Neu laden, wenn der Bot Daten geändert haben könnte (nach jedem Chat-Turn).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataVersion]);

  const COMPANY_KEYS: Array<keyof ProfileFormState> = ['companyName', 'street', 'postalCode', 'city', 'country'];

  const missingFields = useMemo(
    () =>
      (Object.keys(FIELD_LABELS) as Array<keyof ProfileFormState>).filter(
        (key) => !form[key]?.trim()
      ),
    [form]
  );

  // Für den Warn-Banner zählen nur Felder, die der User selbst ändern darf.
  const missingEditable = missingFields.filter(
    (key) => canEditCompany || !COMPANY_KEYS.includes(key)
  );
  const missingCompanyReadonly = canEditCompany
    ? []
    : missingFields.filter((key) => COMPANY_KEYS.includes(key));

  const missingLabel = missingEditable
    .map((key) => FIELD_LABELS[key][isDe ? 'de' : 'en'])
    .join(', ');

  const { countryOptions, fetchCountries } = useCountries();
  useEffect(() => {
    fetchCountries();
  }, []);
  const selectedCountry = countryOptions.find((o) => o.value === form.country) || null;

  const setField = (key: keyof ProfileFormState) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const userChanged =
        form.firstName.trim() !== initial.firstName || form.lastName.trim() !== initial.lastName;
      const companyChanged =
        canEditCompany &&
        (form.companyName.trim() !== initial.companyName ||
        form.street.trim() !== initial.street ||
        form.postalCode.trim() !== initial.postalCode ||
        form.city.trim() !== initial.city ||
        form.country.trim() !== initial.country);

      if (userChanged) {
        const body: Record<string, string> = {};
        if (form.firstName.trim()) body.firstName = form.firstName.trim();
        if (form.lastName.trim()) body.lastName = form.lastName.trim();
        const res = await changeUserInfo(body);
        if (res?.error) throw new Error('user update failed');
      }
      if (companyChanged) {
        const res = await changeOwnCompanyData({
          name: form.companyName.trim() || undefined,
          addressLine1: form.street.trim() || undefined,
          postalCode: form.postalCode.trim() || undefined,
          city: form.city.trim() || undefined,
          country: form.country.trim() || undefined,
        });
        if (res?.error) throw new Error('company update failed');
      }

      const trimmed: ProfileFormState = {
        ...form,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        companyName: form.companyName.trim(),
        street: form.street.trim(),
        postalCode: form.postalCode.trim(),
        city: form.city.trim(),
        country: form.country.trim(),
      };
      setInitial(trimmed);
      setForm(trimmed);
      setSaved(true);

      const savedFields = (Object.keys(FIELD_LABELS) as Array<keyof ProfileFormState>)
        .filter((key) => key !== 'email' && trimmed[key])
        .filter((key) => canEditCompany || !COMPANY_KEYS.includes(key))
        .map((key) => `${FIELD_LABELS[key][isDe ? 'de' : 'en']}: ${trimmed[key]}`)
        .join(', ');
      onSaved(
        isDe
          ? `Ich habe meine Profil- und Firmendaten gerade im Formular gespeichert (${savedFields}). Bitte prüfe kurz mit dem Profil-Vollständigkeitscheck, ob alles vollständig ist, und schlage mir dann die nächsten Schritte vor.`
          : `I just saved my profile and company data in the form (${savedFields}). Please verify with the profile completeness check and then suggest my next steps.`
      );

      // Für Member zählt nur ihr eigener Teil — Firmendaten pflegt ein Admin.
      const complete = (Object.keys(FIELD_LABELS) as Array<keyof ProfileFormState>)
        .filter((key) => canEditCompany || !COMPANY_KEYS.includes(key))
        .every((key) => trimmed[key]?.trim());
      if (complete && onComplete) {
        // Kurz die Bestätigung zeigen, dann weiter zu Schritt 2 (LinkedIn verbinden).
        setTimeout(onComplete, 1400);
      }
    } catch {
      setError(
        isDe
          ? 'Speichern fehlgeschlagen — bitte versuch es noch einmal.'
          : 'Saving failed — please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldProps = (key: keyof ProfileFormState) => {
    const readonly = !canEditCompany && COMPANY_KEYS.includes(key);
    return {
      value: form[key],
      onChange: setField(key),
      label: FIELD_LABELS[key][isDe ? 'de' : 'en'],
      size: 'small' as const,
      fullWidth: true,
      disabled: readonly,
      error: !readonly && !form[key]?.trim(),
    };
  };

  return (
    <Box className={cx(classes.panel, variant === 'workspace' && 'workspace')}>
      {variant === 'panel' && (
      <Box className={classes.header}>
        <Box className={classes.headerTitle}>
          <AssignmentIndIcon className={classes.headerIcon} />
          <Typography variant="subtitle2" className={classes.title}>
            {isDe ? 'Schritt 1: Account-Daten' : 'Step 1: Account details'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label={isDe ? 'Schließen' : 'Close'}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      )}

      {missingEditable.length > 0 && !loading && (
        <Box className={classes.gapBanner}>
          <span className={classes.gapDot}>!</span>
          <Typography variant="caption" className={classes.gapText}>
            {isDe ? 'Fehlt in diesem Schritt' : 'Missing in this step'}: {missingLabel}
          </Typography>
        </Box>
      )}
      {missingEditable.length === 0 && !loading && (
        <Box className={cx(classes.gapBanner, classes.doneBanner)}>
          <span className={cx(classes.gapDot, classes.doneDot)}>✓</span>
          <Typography variant="caption" className={cx(classes.gapText, classes.doneText)}>
            {isDe ? 'Alle Angaben vollständig' : 'All fields complete'}
          </Typography>
        </Box>
      )}

      <Box className={classes.body}>
        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <>
            <Box className={classes.card}>
              <Typography variant="caption" className={classes.sectionLabel}>
                {isDe ? 'Persönliche Daten' : 'Personal data'}
              </Typography>
              <TextField {...fieldProps('firstName')} />
              <TextField {...fieldProps('lastName')} />
              <TextField
                {...fieldProps('email')}
                disabled
                helperText={
                  isDe ? 'Die E-Mail kommt aus deinem Login.' : 'The email comes from your login.'
                }
              />
              <TextField
                select
                value={uiLanguage}
                onChange={handleUiLanguageChange}
                label={isDe ? 'Sprache / Language' : 'Language / Sprache'}
                size="small"
                fullWidth
                helperText={
                  isDe
                    ? 'Sprache der Oberfläche — wird in deinem Profil gespeichert.'
                    : 'Interface language — stored in your profile.'
                }
              >
                <MenuItem value="de">Deutsch</MenuItem>
                <MenuItem value="en">English</MenuItem>
              </TextField>
            </Box>

            {canEditCompany && (
            <Box className={classes.card}>
              <Typography variant="caption" className={classes.sectionLabel}>
                {isDe ? 'Firmendaten' : 'Company data'}
              </Typography>
              {!canEditCompany && (
                <Typography variant="caption" color="textSecondary">
                  {isDe
                    ? missingCompanyReadonly.length > 0
                      ? 'Nur Admins können Firmendaten ändern — bitte einen Workspace-Admin, die fehlenden Angaben zu ergänzen.'
                      : 'Nur Admins können Firmendaten ändern.'
                    : missingCompanyReadonly.length > 0
                      ? 'Only admins can change company data — ask a workspace admin to complete the missing fields.'
                      : 'Only admins can change company data.'}
                </Typography>
              )}
              <TextField {...fieldProps('companyName')} />
              <TextField {...fieldProps('street')} />
              <Box className={classes.row}>
                <TextField {...fieldProps('postalCode')} />
                <TextField {...fieldProps('city')} />
              </Box>
              <Autocomplete
                options={countryOptions}
                value={selectedCountry}
                onChange={(_e, option) => {
                  setForm((prev) => ({ ...prev, country: option?.value ? String(option.value) : '' }));
                  setSaved(false);
                }}
                getOptionLabel={(o) => String(o.label)}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                disabled={!canEditCompany}
                size="small"
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={FIELD_LABELS.country[isDe ? 'de' : 'en']}
                    placeholder={isDe ? 'Land auswählen' : 'Select country'}
                    error={canEditCompany && !form.country}
                  />
                )}
              />
            </Box>
            )}

            {error && (
              <Typography variant="caption" className={classes.error}>
                {error}
              </Typography>
            )}

            <Box className={classes.footer}>
              <Typography variant="caption" className={classes.footerHint}>
                {saved
                  ? isDe
                    ? 'Gespeichert — der Assistent meldet sich im Chat.'
                    : 'Saved — the assistant will follow up in the chat.'
                  : isDe
                    ? 'Der Assistent prüft die Daten nach dem Speichern.'
                    : 'The assistant verifies the data after saving.'}
              </Typography>
              <button
                type="button"
                className={classes.saveButton}
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? isDe
                    ? 'Speichert…'
                    : 'Saving…'
                  : isDe
                    ? 'Weiter'
                    : 'Next'}
              </button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  panel: {
    width: 380,
    flexShrink: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: `1px solid ${theme.palette.greys.lighter}`,
    backgroundColor: theme.palette.backgrounds.main_new,
    boxSizing: 'border-box',
    '&.workspace': {
      width: '100%',
      maxWidth: 620,
      height: 'auto',
      borderLeft: 'none',
      backgroundColor: 'transparent',
    },
  },
  header: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '13px 16px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
    backgroundColor: theme.palette.background.paper,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  headerIcon: {
    color: theme.palette.primary.main,
    fontSize: '1.25rem',
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  gapBanner: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 16px',
    backgroundColor: '#FFF8EC',
    borderBottom: '1px solid #F6E4C4',
  },
  gapDot: {
    flexShrink: 0,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: '#FFB547',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'grid',
    placeItems: 'center',
  },
  gapText: {
    color: '#8A6320',
    lineHeight: 1.4,
  },
  doneBanner: {
    backgroundColor: '#EAFBF4',
    borderBottom: '1px solid #C6EEDD',
  },
  doneDot: {
    backgroundColor: '#05CD99',
  },
  doneText: {
    color: '#0B7050',
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 32,
  },
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: theme.palette.text.secondary,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.6fr',
    gap: 12,
  },
  error: {
    color: theme.palette.error.main,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  footerHint: {
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  saveButton: {
    flexShrink: 0,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 10,
    padding: '9px 18px',
    '&:hover': {
      opacity: 0.9,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'default',
    },
  },
}));

export default ProfileDataPanel;
